import {
    type ReactNode,
    useCallback,
    useState,
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IDropdownMenuSheetProps from "./type";
import type {
    DropdownMenuSheetRecordType
} from "./type";
import stylesheet from "./stylesheet";
import {
    resolveDropdownMenuSheetPage
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useDropdownMenuContext,
    DropdownMenuContext
} from "../../context";
import type {
    ActionSheetEntryType
} from "../../../bottomSheet/components/actionSheet/type";
import {
    ChevronRight as ChevronRightIcon
} from "lucide-react-native";
import {
    getMirrorStyle
} from "../../../../helpers/direction";
import ActionSheet from "../../../bottomSheet/components/actionSheet";
import Switch from "../../../switch";

const DropdownMenuSheet: FC<IDropdownMenuSheetProps> = ({
    customTheme: customThemeProp,
    children
}) => {
    const rootContext = useDropdownMenuContext({
        part: "Content"
    });

    const customTheme = customThemeProp ?? rootContext?.customTheme;

    const {
        iconSizes,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const [
        path,
        setPath
    ] = useState<Array<string>>([]);

    const handleOpenChange = rootContext?.handleOpenChange;

    const page = useMemo(() => resolveDropdownMenuSheetPage({
        title: rootContext?.title,
        children,
        path
    }), [
        rootContext?.title,
        children,
        path
    ]);

    const handleBack = useCallback(() => {
        setPath((previousPath) => previousPath.slice(0, -1));
    }, []);

    const handleClosed = useCallback(() => {
        setPath([]);

        handleOpenChange?.(false);
    }, [
        handleOpenChange
    ]);

    if(!rootContext) {
        return null;
    }

    const renderTrailing = (record: DropdownMenuSheetRecordType): ReactNode => {
        if(record.trailing === "switch") {
            return <View
                style={stylesheet.switchPreview}
                aria-hidden={true}
            >
                <Switch
                    aria-label={typeof record.item?.label === "string" ? record.item.label : undefined}
                    isChecked={record.isChecked}
                    customTheme={customTheme}
                    spreadBehaviour="free"
                    tabIndex={-1}
                />
            </View>;
        }

        if(record.trailing === "chevron") {
            return <View
                style={[
                    stylesheet.chevron,
                    getMirrorStyle({
                        direction
                    })
                ]}
                aria-hidden={true}
            >
                <ChevronRightIcon
                    color={colors.content.icon.mid}
                    size={iconSizes.small}
                />
            </View>;
        }

        return undefined;
    };

    const getEntry = (record: DropdownMenuSheetRecordType): ActionSheetEntryType => {
        if(record.heading !== undefined || !record.item) {
            return {
                isReserveLeading: record.isReserveLeading,
                heading: record.heading,
                key: record.key
            };
        }

        const {
            subChildren,
            onPress,
            key
        } = record;

        return {
            ...record.item,
            onPress: subChildren === undefined ? onPress : () => {
                setPath((previousPath) => [
                    ...previousPath,
                    key
                ]);
            },
            isCloseOnPress: record.isCloseOnPress,
            trailing: renderTrailing(record),
            key
        };
    };

    return <ActionSheet
        onBack={path.length ? handleBack : undefined}
        customLocalize={rootContext.customLocalize}
        items={page.records.map(getEntry)}
        isActive={rootContext.isOpen}
        backTitle={page.backTitle}
        customTheme={customTheme}
        onClosed={handleClosed}
        title={page.title}
    >
        <DropdownMenuContext.Provider
            value={rootContext}
        >
            {page.extras}
        </DropdownMenuContext.Provider>
    </ActionSheet>;
};
export default DropdownMenuSheet;
